import { tokens } from '@neo4j-ndl/base';
import { Drawer, Tabs, Tag, Typography, useMediaQuery } from '@neo4j-ndl/react';
import { ExclamationTriangleIconOutline } from '@neo4j-ndl/react/icons';
import { FleetManagementTypes, consoleApi, fleetManagementApi, useActiveProject } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { ApiErrorBanner } from '../../shared/components';
import { timePeriodFormatter } from '../../shared/ui-helpers';
import { DeploymentStatus } from '../shared/components/deployment-status';
import { PropertyDisplay, PropertyNodeDisplay } from '../shared/components/property-display';
import type { Neo4jConfigWithServerInfo } from './deployment-config';
import { DeploymentConfig } from './deployment-config';
import { TokenActions, TokenStatus, TokenUsage } from './token-management';

interface LicenseDetailsProps {
  license?: FleetManagementTypes.License;
  serverAddress?: string;
}

const LicenseDetails = ({ license, serverAddress }: LicenseDetailsProps) => {
  if (isNullish(license)) {
    return (
      <Tag type="destructive" size="medium" className="flex w-fit items-center gap-1">
        <ExclamationTriangleIconOutline className="h-4 w-4" />
        <Typography variant="body-medium">Missing license information</Typography>
      </Tag>
    );
  }

  let typeText: string;
  switch (license.type) {
    case FleetManagementTypes.Api.LICENSE_TYPE.UNSUPPORTED:
      typeText = 'License information for the current version is not supported';
      break;
    case FleetManagementTypes.Api.LICENSE_TYPE.INCONSISTENT:
      typeText = 'License is inconsistent between servers';
      break;
    default:
      typeText = `${license.type}`;
      break;
  }

  const warningState =
    license.state === FleetManagementTypes.Api.LICENSE_STATE.INCONSISTENT ||
    license.state === FleetManagementTypes.Api.LICENSE_STATE.UNKNOWN ||
    license.state === FleetManagementTypes.Api.LICENSE_STATE.EXPIRED ||
    license.state === FleetManagementTypes.Api.LICENSE_STATE.NOT_ACCEPTED ||
    license.state === FleetManagementTypes.Api.LICENSE_STATE.INVALID;

  return (
    <>
      {isNotNullish(serverAddress) && <PropertyDisplay label="Server" text={serverAddress} />}
      <PropertyDisplay label="Type" text={typeText} />
      {warningState && (
        <Tag type="destructive" size="medium" className="flex w-fit items-center gap-1">
          <ExclamationTriangleIconOutline className="h-4 w-4" />
          <Typography variant="body-medium">{license.state}</Typography>
        </Tag>
      )}
      {!warningState && <PropertyDisplay label="State" text={license.state} />}
      {license.daysLeftOnTrial && (
        <>
          {!license.totalTrialDays ? (
            <PropertyDisplay label="License valid for" text={`${license.daysLeftOnTrial} days`} />
          ) : (
            <PropertyDisplay
              label="Trial"
              text={`${license.daysLeftOnTrial} days left of ${license.totalTrialDays} days`}
            />
          )}
        </>
      )}
    </>
  );
};

interface LicenseProps {
  license?: FleetManagementTypes.License;
  productName: string;
  servers?: FleetManagementTypes.Server[];
}

const License = ({ license, productName, servers }: LicenseProps) => {
  const isInconsistent = license?.state === FleetManagementTypes.Api.LICENSE_STATE.INCONSISTENT;

  return (
    <div className="border-neutral-border-weak mb-3 gap-x-4 gap-y-1 rounded-[8px] border px-3 py-3">
      <h6 className="font-semibold">{productName}</h6>
      <LicenseDetails license={license} />
      {isInconsistent && servers && (
        <>
          <div className="mt-2">
            <h6 className="mb-1 text-sm font-medium">Server Licenses</h6>
            {servers
              .map((server) => server)
              .sort((a, b) => a.displayName.localeCompare(b.displayName))
              .map((server) => {
                let serverLicense;
                if (productName === 'Neo4j') {
                  serverLicense = server.license;
                } else if (productName === 'Graph Data Science') {
                  serverLicense = server.gdsLicense;
                } else {
                  serverLicense = server.bloomLicense;
                }

                return (
                  <div key={server.serverId} className="border-neutral-border-weak mb-2 border-l-2 pl-2">
                    <LicenseDetails license={serverLicense} serverAddress={server.displayName} />
                  </div>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
};

const TokenDetails = ({
  deploymentId,
  token,
  name,
  creator,
}: {
  deploymentId: string;
  token: FleetManagementTypes.Token | undefined;
  name: string;
  creator: string | undefined;
}) => {
  let autoRotated = undefined;
  if (isNotNullish(token)) {
    autoRotated = token.autoRotated ? 'Yes' : 'No';
  }
  return (
    <div className="border-neutral-border-weak mb-3 gap-x-4 gap-y-1 rounded-[8px] border px-3 py-3">
      <div className="flex flex-row justify-between">
        <div className="flex flex-col gap-2">
          <TokenStatus token={token} />
          <PropertyNodeDisplay label="Status">
            <TokenUsage token={token} />
          </PropertyNodeDisplay>
          <PropertyDisplay label="Registered by" text={creator ?? 'unknown user'} />
          <PropertyDisplay
            label="Creation time"
            text={isNotNullish(token?.creationTime) ? timePeriodFormatter(new Date(token.creationTime)) : '-'}
          />
          <PropertyDisplay
            label="Expires on"
            text={isNotNullish(token?.expiryTime) ? timePeriodFormatter(new Date(token.expiryTime)) : '-'}
          />
          <PropertyDisplay label="Auto-rotated" text={autoRotated} />
        </div>
        <TokenActions token={token} deploymentId={deploymentId} />
      </div>
    </div>
  );
};

interface DeploymentInspectDrawerProps {
  deployment: FleetManagementTypes.DeploymentDetails;
  onClose: () => void;
}

export enum FLEET_INSTANCE_DETAILS_TAB {
  OVERVIEW = 'overview',
  CONFIG = 'config',
}

export const DeploymentInspectDrawer = ({ deployment, onClose }: DeploymentInspectDrawerProps) => {
  const { breakpoints } = tokens;
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.sm})`);
  const activeProject = useActiveProject();
  const [selectedTab, setSelectedTab] = useState(FLEET_INSTANCE_DETAILS_TAB.OVERVIEW);
  const getProjectMembersRes = consoleApi.useListProjectMembersQuery(activeProject.id);
  const projectMembers = getProjectMembersRes.data;
  const deploymentCreator = projectMembers?.find((member) => member.userId === deployment.createdBy)?.email;
  const tokenCreator = projectMembers?.find((member) => member.userId === deployment.token?.createdBy)?.email;

  const [neo4jConfigs, setNeo4jConfigs] = useState<Neo4jConfigWithServerInfo[]>([]);
  const getNeo4jConfigsRes = fleetManagementApi.useGetNeo4jConfigsQuery({
    deploymentId: deployment.deploymentId,
    projectId: activeProject.id,
  });

  useEffect(() => {
    if (getNeo4jConfigsRes.data && !getNeo4jConfigsRes.isFetching) {
      const configs = getNeo4jConfigsRes.data.map((neo4jConfig) => {
        const serverIdFromResponse = neo4jConfig.serverId;
        const maybeDeploymentServer = deployment.dbms?.servers.find(
          (server) => server.serverId === serverIdFromResponse,
        );

        let displayName = neo4jConfig.serverId;
        if (maybeDeploymentServer !== undefined) {
          displayName = maybeDeploymentServer.displayName;
        }

        const config: Neo4jConfigWithServerInfo = {
          serverId: neo4jConfig.serverId,
          serverName: displayName,
          neo4jConfig: neo4jConfig.config,
        };
        return config;
      });

      configs.sort((a, b) => a.serverName.localeCompare(b.serverName));

      setNeo4jConfigs(configs);
    }
  }, [getNeo4jConfigsRes.data, getNeo4jConfigsRes.isFetching, deployment.dbms?.servers]);

  // Refetch config only when server data changes
  useEffect(() => {
    void getNeo4jConfigsRes.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deployment.dbms?.servers]);

  const defaultWidth = useMemo(() => {
    if (isMobile) {
      return window.innerWidth;
    }
    return 720;
  }, [isMobile]);

  return (
    <Drawer
      isExpanded
      onExpandedChange={onClose}
      position="right"
      className="!border-l"
      isResizeable
      resizeableProps={{
        defaultSize: { height: '100%', width: defaultWidth },
        minWidth: defaultWidth,
      }}
    >
      <Drawer.Header className="flex items-center gap-4">
        <h5 className="ml-1">{deployment.name}</h5>
        <DeploymentStatus status={deployment.status} />
      </Drawer.Header>
      <Drawer.Body className="flex flex-col">
        <div className="flex flex-grow flex-col">
          <Tabs value={selectedTab} onChange={setSelectedTab} size="small">
            <Tabs.Tab tabId={FLEET_INSTANCE_DETAILS_TAB.OVERVIEW}>Overview</Tabs.Tab>
            <Tabs.Tab tabId={FLEET_INSTANCE_DETAILS_TAB.CONFIG}>Active configuration</Tabs.Tab>
          </Tabs>
          <div className="mt-4 flex-grow">
            <Tabs.TabPanel tabId={FLEET_INSTANCE_DETAILS_TAB.OVERVIEW} value={selectedTab}>
              <div className="mb-6 flex flex-col gap-2">
                <PropertyDisplay label="Version" text={deployment.dbms?.version} />
                <PropertyDisplay label="Edition" text={deployment.dbms?.edition} />
                <PropertyDisplay label="Packaging" text={deployment.dbms?.packaging} />
                <PropertyDisplay
                  label="Servers"
                  text={deployment.dbms?.servers
                    .map((server) => server.displayName)
                    .sort((a, b) => a.localeCompare(b))
                    .join(', ')}
                />
                <PropertyDisplay label="Registered by" text={deploymentCreator ?? 'unknown user'} />
              </div>
              <div className="mb-6">
                <h6 className="mb-2 font-semibold">Token</h6>
                <TokenDetails
                  deploymentId={deployment.deploymentId}
                  token={deployment.token}
                  name={deployment.name}
                  creator={tokenCreator}
                />
              </div>
              {deployment.status !== FleetManagementTypes.Api.DEPLOYMENT_STATUS.UNMONITORED && (
                <div className="mb-6">
                  <h6 className="mb-2 font-semibold">Licenses</h6>
                  <License license={deployment.dbms?.license} productName="Neo4j" servers={deployment.dbms?.servers} />
                  {deployment.dbms?.gdsLicense && (
                    <License
                      license={deployment.dbms.gdsLicense}
                      productName="Graph Data Science"
                      servers={deployment.dbms.servers}
                    />
                  )}
                  {deployment.dbms?.bloomLicense && (
                    <License
                      license={deployment.dbms.bloomLicense}
                      productName="Bloom"
                      servers={deployment.dbms.servers}
                    />
                  )}
                </div>
              )}
            </Tabs.TabPanel>
            <Tabs.TabPanel tabId={FLEET_INSTANCE_DETAILS_TAB.CONFIG} value={selectedTab}>
              {getNeo4jConfigsRes.isError && <ApiErrorBanner className="m-4" error={getNeo4jConfigsRes.error} />}
              {!getNeo4jConfigsRes.isError && (
                <DeploymentConfig neo4jConfigs={neo4jConfigs} isLoading={getNeo4jConfigsRes.isLoading} />
              )}
            </Tabs.TabPanel>
          </div>
        </div>
      </Drawer.Body>
    </Drawer>
  );
};

interface LicenseIssueBadgeProps {
  onClick: () => void;
}

export const LicenseIssueBadge = ({ onClick }: LicenseIssueBadgeProps) => {
  return (
    <div onClick={onClick} className="cursor-pointer">
      <Tag type="destructive" size="medium" className="flex w-fit items-center gap-1">
        <ExclamationTriangleIconOutline className="h-4 w-4" />
        <Typography variant="body-medium">License issue detected</Typography>
      </Tag>
    </div>
  );
};

export const OutdatedPluginBadge = ({ deployment }: { deployment: FleetManagementTypes.DeploymentDetails }) => {
  const navigate = useNavigate();

  const openDetails = useCallback(() => {
    void navigate(`${deployment.deploymentId}/details/`);
  }, [deployment.deploymentId, navigate]);

  return (
    <div onClick={openDetails} className="cursor-pointer">
      <Tag type="destructive" size="medium" className="flex w-fit items-center gap-1">
        <ExclamationTriangleIconOutline className="h-4 w-4" />
        <Typography variant="body-medium">Outdated plugin</Typography>
      </Tag>
    </div>
  );
};
